import type { AdminCustomerDto, AdminUserDetailDto, AdminUserDto } from "@bafe/api-client";
import { ORGANISATION_ROLES } from "@bafe/types";
import { Select } from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "../../lib/api.js";
import { presentError } from "../../lib/errors.js";
import { formatDate } from "../../lib/format.js";
import { useSession } from "../../lib/session.js";
import {
	HubOperationBar,
	HubOperationColumnMenu,
	HubOperationFormField,
	HubOperationFormRow,
	HubOperationFormStack,
	HubOperationListTable,
	HubOperationModal,
	HubOperationScreen,
	HubOperationSearchField,
	HubOperationSearchToggle,
	HubOperationTableEmptyRow,
	HubOperationTablePagination,
	HubOperationToolbar,
	useHubOperationSearchFocus,
} from "../shell/hub-operation-shell.js";
import { HUB_OPERATION_PAGE_SIZES, hubOperationPageCount } from "../shell/hub-operation-pagination.js";

const ASSIGNABLE_ROLES = ORGANISATION_ROLES.filter((role) => role !== "owner");

const USER_STATUS_OPTIONS = [
	["active", "Active"],
	["invited", "Invited"],
	["suspended", "Suspended"],
	["closed", "Closed"],
] as const;

type UserColumnId = "name" | "email" | "status" | "workspaces" | "lastLogin";

const USER_COLUMNS: Array<{ id: UserColumnId; label: string }> = [
	{ id: "name", label: "Name" },
	{ id: "email", label: "Email" },
	{ id: "status", label: "Status" },
	{ id: "workspaces", label: "Workspaces" },
	{ id: "lastLogin", label: "Last sign-in" },
];

const DEFAULT_VISIBILITY: Record<UserColumnId, boolean> = {
	name: true,
	email: true,
	status: true,
	workspaces: true,
	lastLogin: true,
};

function humaniseStatus(value: string): string {
	return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

type MembershipDraft = { organisationId: string; organisationName: string; role: string };

function workspaceCell(user: AdminUserDto): string {
	if (user.workspaceCount === 0) return "None";
	if (user.workspaceCount === 1) {
		const summary = user.workspaceSummary ?? "";
		const name = summary.split(" · ")[0] ?? "1 workspace";
		return name;
	}
	return `${user.workspaceCount} workspaces`;
}

export function PlatformUserManagement() {
	const session = useSession();
	const queryClient = useQueryClient();
	const [query, setQuery] = useState("");
	const [searchOpen, setSearchOpen] = useState(false);
	const searchRef = useRef<HTMLInputElement>(null);
	useHubOperationSearchFocus(searchOpen, searchRef);
	const [columnVisibility, setColumnVisibility] = useState(DEFAULT_VISIBILITY);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState<(typeof HUB_OPERATION_PAGE_SIZES)[number]>(20);

	const [modalOpen, setModalOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [formError, setFormError] = useState("");
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<(typeof USER_STATUS_OPTIONS)[number][0]>("invited");
	const [memberships, setMemberships] = useState<MembershipDraft[]>([]);
	const [workspaceQuery, setWorkspaceQuery] = useState("");

	const users = useQuery({
		queryKey: ["admin-users", query, page, pageSize],
		queryFn: () => api.adminSearchUsers(query, page, pageSize),
		enabled: Boolean(session.data?.platformOwner),
	});

	const detail = useQuery({
		queryKey: ["admin-user", editingId],
		queryFn: () => api.adminGetUser(editingId!),
		enabled: modalOpen && Boolean(editingId),
	});

	const workspaces = useQuery({
		queryKey: ["admin-workspace-picker", workspaceQuery],
		queryFn: () => api.adminSearchCustomers(workspaceQuery, 1),
		enabled: modalOpen && Boolean(session.data?.platformOwner),
	});

	const total = users.data?.meta.total ?? 0;
	const totalPages = hubOperationPageCount(total, pageSize);

	useEffect(() => {
		if (!modalOpen || !editingId || !detail.data) return;
		setFullName(detail.data.fullName);
		setEmail(detail.data.email);
		setStatus(detail.data.status as (typeof USER_STATUS_OPTIONS)[number][0]);
		setMemberships(
			detail.data.memberships.map((row) => ({
				organisationId: row.organisationId,
				organisationName: row.organisationName,
				role: row.role,
			})),
		);
	}, [modalOpen, editingId, detail.data]);

	function openCreate() {
		setEditingId(null);
		setFullName("");
		setEmail("");
		setStatus("invited");
		setMemberships([]);
		setWorkspaceQuery("");
		setFormError("");
		setModalOpen(true);
	}

	function openEdit(user: AdminUserDto) {
		setEditingId(user.userId);
		setFullName(user.fullName);
		setEmail(user.email);
		setStatus(user.status as (typeof USER_STATUS_OPTIONS)[number][0]);
		setWorkspaceQuery("");
		setFormError("");
		setModalOpen(true);
	}

	function closeModal() {
		if (saveUser.isPending) return;
		setModalOpen(false);
		setEditingId(null);
		setFormError("");
	}

	const saveUser = useMutation({
		mutationFn: async () => {
			const membershipPayload = memberships.map((row) => ({
				organisationId: row.organisationId,
				role: row.role,
			}));
			if (!editingId) {
				return api.adminCreateUser({
					fullName: fullName.trim(),
					email: email.trim(),
					status: status === "closed" ? "invited" : status,
					memberships: membershipPayload,
				});
			}
			await api.adminUpdateUser(editingId, {
				fullName: fullName.trim(),
				status,
			});
			return api.adminPutUserMemberships(editingId, membershipPayload);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
			await queryClient.invalidateQueries({ queryKey: ["admin-user"] });
			setModalOpen(false);
			setEditingId(null);
		},
		onError: (error) => setFormError(presentError(error).title),
	});

	const visibleColumns = USER_COLUMNS.filter((column) => columnVisibility[column.id]);
	const rows = users.data?.data ?? [];

	const workspaceOptions = useMemo(() => {
		const list = workspaces.data?.data ?? [];
		const selected = new Set(memberships.map((row) => row.organisationId));
		return list.filter((row) => !selected.has(row.organisationId));
	}, [memberships, workspaces.data?.data]);

	if (!session.data) return <p className="empty">Loading users.</p>;
	if (!session.data.platformOwner) {
		return <p className="empty">Only platform operators can manage users here.</p>;
	}

	return (
		<HubOperationScreen>
			<HubOperationBar sub="Every account on this server. Add users, set status, and assign workspaces.">
				<HubOperationToolbar
					searchSlot={
						<HubOperationSearchField
							id="platform-users-search"
							ariaLabel="Search users"
							placeholder="Search name or email"
							searchOpen={searchOpen}
							searchQuery={query}
							searchRef={searchRef}
							onQueryChange={(value) => {
								setQuery(value);
								setPage(1);
							}}
							onEscape={() => setSearchOpen(false)}
						/>
					}
					tools={
						<>
							<HubOperationSearchToggle
								searchOpen={searchOpen}
								searchActive={query.trim().length > 0}
								ariaLabel="Search users"
								onToggle={() => setSearchOpen((open) => !open)}
							/>
							<HubOperationColumnMenu
								columns={USER_COLUMNS}
								visibility={columnVisibility}
								onToggle={(id, checked) => setColumnVisibility((prev) => ({ ...prev, [id]: checked }))}
							/>
						</>
					}
					primaryAction={
						<button className="btn" type="button" onClick={openCreate}>
							Add user
						</button>
					}
				/>
			</HubOperationBar>

			{users.isPending && <p className="empty">Loading users.</p>}
			{users.isError && <p className="err">{presentError(users.error).title}</p>}

			{users.data && (
				<HubOperationListTable>
					<div className="table-wrap hub-inventory-table-wrap">
						<table className="hub-inventory-table">
							<thead>
								<tr>
									{visibleColumns.map((column) => (
										<th key={column.id} scope="col">{column.label}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 ? (
									<HubOperationTableEmptyRow colSpan={visibleColumns.length}>
										No users match this search.
									</HubOperationTableEmptyRow>
								) : (
									rows.map((user) => (
										<tr
											key={user.userId}
											className="cursor-pointer"
											tabIndex={0}
											onClick={() => openEdit(user)}
											onKeyDown={(event) => {
												if (event.key === "Enter" || event.key === " ") {
													event.preventDefault();
													openEdit(user);
												}
											}}
										>
											{columnVisibility.name ? <td>{user.fullName}</td> : null}
											{columnVisibility.email ? <td className="quiet">{user.email}</td> : null}
											{columnVisibility.status ? <td className="quiet">{humaniseStatus(user.status)}</td> : null}
											{columnVisibility.workspaces ? (
												<td className="quiet" title={user.workspaceSummary ?? undefined}>
													{workspaceCell(user)}
												</td>
											) : null}
											{columnVisibility.lastLogin ? (
												<td className="quiet">{formatDate(user.lastLoginAt)}</td>
											) : null}
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					<HubOperationTablePagination
						total={total}
						page={page}
						pageSize={pageSize}
						totalPages={totalPages}
						onPage={setPage}
						onPageSize={setPageSize}
						totalNoun="users"
					/>
				</HubOperationListTable>
			)}

			<UserEditorModal
				open={modalOpen}
				isCreate={!editingId}
				pending={saveUser.isPending}
				error={formError}
				fullName={fullName}
				email={email}
				status={status}
				memberships={memberships}
				workspaceQuery={workspaceQuery}
				workspaceOptions={workspaceOptions}
				detail={detail.data}
				onClose={closeModal}
				onSubmit={() => saveUser.mutate()}
				onFullNameChange={setFullName}
				onEmailChange={setEmail}
				onStatusChange={setStatus}
				onWorkspaceQueryChange={setWorkspaceQuery}
				onMembershipsChange={setMemberships}
			/>
		</HubOperationScreen>
	);
}

function UserEditorModal({
	open,
	isCreate,
	pending,
	error,
	fullName,
	email,
	status,
	memberships,
	workspaceQuery,
	workspaceOptions,
	detail,
	onClose,
	onSubmit,
	onFullNameChange,
	onEmailChange,
	onStatusChange,
	onWorkspaceQueryChange,
	onMembershipsChange,
}: {
	open: boolean;
	isCreate: boolean;
	pending: boolean;
	error: string;
	fullName: string;
	email: string;
	status: string;
	memberships: MembershipDraft[];
	workspaceQuery: string;
	workspaceOptions: AdminCustomerDto[];
	detail: AdminUserDetailDto | undefined;
	onClose: () => void;
	onSubmit: () => void;
	onFullNameChange: (value: string) => void;
	onEmailChange: (value: string) => void;
	onStatusChange: (value: (typeof USER_STATUS_OPTIONS)[number][0]) => void;
	onWorkspaceQueryChange: (value: string) => void;
	onMembershipsChange: (value: MembershipDraft[]) => void;
}) {
	function addWorkspace(row: AdminCustomerDto) {
		onMembershipsChange([
			...memberships,
			{ organisationId: row.organisationId, organisationName: row.name, role: "user" },
		]);
		onWorkspaceQueryChange("");
	}

	return (
		<HubOperationModal
			open={open}
			title={isCreate ? "Add user" : "User details"}
			error={error}
			pending={pending}
			action={isCreate ? "Create user" : "Save changes"}
			hubForm
			onClose={onClose}
			onSubmit={() => onSubmit()}
		>
			<HubOperationFormStack>
				<HubOperationFormRow>
					<HubOperationFormField label="Name" htmlFor="platform-user-name">
						<input
							id="platform-user-name"
							name="fullName"
							value={fullName}
							required
							autoComplete="name"
							onChange={(event) => onFullNameChange(event.target.value)}
						/>
					</HubOperationFormField>
					<HubOperationFormField label="Email" htmlFor="platform-user-email">
						<input
							id="platform-user-email"
							name="email"
							type="email"
							value={email}
							required
							readOnly={!isCreate}
							autoComplete="email"
							onChange={(event) => onEmailChange(event.target.value)}
						/>
					</HubOperationFormField>
				</HubOperationFormRow>
				<HubOperationFormRow>
					<HubOperationFormField label="Account status" htmlFor="platform-user-status">
						<Select
							id="platform-user-status"
							value={status}
							onChange={(event) =>
								onStatusChange(event.target.value as (typeof USER_STATUS_OPTIONS)[number][0])
							}
						>
							{USER_STATUS_OPTIONS.map(([id, label]) => (
								<option key={id} value={id} disabled={isCreate && id === "closed"}>
									{label}
								</option>
							))}
						</Select>
					</HubOperationFormField>
				</HubOperationFormRow>

				<div className="flex flex-col gap-2">
					<span className="text-[13px] font-medium">Workspace access</span>
					{memberships.length === 0 ? (
						<p className="quiet m-0 text-sm">No workspaces assigned yet.</p>
					) : (
						<ul className="m-0 flex list-none flex-col gap-2 p-0">
							{memberships.map((row) => (
								<li key={row.organisationId} className="grid two gap-2 items-center">
									<span className="truncate text-sm" title={row.organisationName}>
										{row.organisationName}
									</span>
									<div className="flex gap-2">
										<Select
											aria-label={`Role in ${row.organisationName}`}
											value={row.role}
											onChange={(event) => {
												onMembershipsChange(
													memberships.map((item) =>
														item.organisationId === row.organisationId
															? { ...item, role: event.target.value }
															: item,
													),
												);
											}}
										>
											{ASSIGNABLE_ROLES.map((role) => (
												<option key={role} value={role}>
													{humaniseStatus(role)}
												</option>
											))}
										</Select>
										<button
											type="button"
											className="btn ghost"
											onClick={() =>
												onMembershipsChange(
													memberships.filter((item) => item.organisationId !== row.organisationId),
												)
											}
										>
											Remove
										</button>
									</div>
								</li>
							))}
						</ul>
					)}

					<label className="sr-only" htmlFor="platform-user-workspace-search">
						Search workspaces to add
					</label>
					<input
						id="platform-user-workspace-search"
						className="search"
						type="search"
						placeholder="Search workspaces to add"
						value={workspaceQuery}
						onChange={(event) => onWorkspaceQueryChange(event.target.value)}
					/>
					{workspaceOptions.length > 0 && workspaceQuery.trim() ? (
						<ul className="m-0 max-h-40 list-none overflow-auto rounded border p-0">
							{workspaceOptions.slice(0, 8).map((row) => (
								<li key={row.organisationId}>
									<button
										type="button"
										className="select-item w-full text-left"
										onClick={() => addWorkspace(row)}
									>
										{row.name}
									</button>
								</li>
							))}
						</ul>
					) : null}
				</div>

				{!isCreate && detail?.platformOperator ? (
					<p className="quiet m-0 text-sm">This account is a platform operator.</p>
				) : null}
				{isCreate ? (
					<p className="quiet m-0 text-sm">
						We email a secure link so they can set their password. Workspace memberships take effect once
						they sign in.
					</p>
				) : null}
			</HubOperationFormStack>
		</HubOperationModal>
	);
}
